<template>
	<div class="customer-area-page jobs">
		<b-container>
			<b-row>
				<b-col md="3">
					<Navigation
						page="job-profiles"
						:only="hideNavigation ? ['logout'] : undefined"
						:sub-link-active-key="jobProfile.id"
						:can-add-job-profile="!isOnboarding"
					/>
				</b-col>
				<b-col md="6">
					<Loading v-if="loadingJobProfile && !initiallyLoaded"/>
					<template v-else-if="customer">
						<h1 v-if="isOnboarding">Hallo {{ customer.firstName }}!</h1>
						<h1 v-else>Deine Matches zum Job:</h1>
						<div v-if="!hasName">
							<b-row>
								<b-col md="8">
									<div class="title-container">
										<SetJobProfileName
											:job-profile="jobProfileListItem.jobProfile"
											@save="onSaveName"
										/>
									</div>
								</b-col>
							</b-row>
						</div>
						<div v-else-if="isOnboarding">
							<Onboarding
								:job-profile="jobProfileListItem"
								:is-initial="hasOnlyOneJobProfile"
								@close="onCloseOnboarding"
							/>
						</div>
						<div v-else class="job-listing">
							<div :class="['job-listing--items', { 'job-listing--items-loading': loadingJobProfile }]">
								<div class="job-listing--item">
									<JobProfileHead
										:job-profile-list-item="jobProfileListItem"
										@publish="loadJobProfile"
										@unpublish="loadJobProfile"
										@save="loadJobProfile"
										@closeMessages="loadJobProfile"
										@closeAppointments="loadJobProfile"
										@loadFavorites="onlyFavorites = true"
									/>
									<JobProfileFooter
										:job-profile-list-item="jobProfileListItem"
										ref="jobProfileFooter"
										@load="resetOnlyFavoritesAndLoadJobProfile"
										@publish="onPublish"
										@unpublish="onUnpublish"
									/>
									<JonInfo v-if="!isPublished" class="mt-3">
										<template #message>
											Du kannst den Job nun noch bearbeiten und weitere Filter setzen.
											Sobald du fertig bist, klicke
											<a href="#"
											   @click.prevent="$refs.jobProfileFooter.showPublishPopUp()">hier</a>
											um den Job zu veröffentlichen.
										</template>
									</JonInfo>
									<List
										v-if="(isPublished || internal) && hasMatches"
										ref="list"
										:job-profile-list-item="jobProfileListItem"
										:only-favorites="onlyFavorites"
									/>
								</div>
							</div>
						</div>
					</template>
				</b-col>
			</b-row>
		</b-container>
	</div>
</template>

<script>
import Navigation from "@/vue/customer-area/Navigation"
import Loading from "@/vue/common/Loading";
import GetList from "@/api/customer/job-profile/GetList";
import {mapActions, mapState} from "vuex";
import {isEmptyOrNull} from "@/helper/String";
import Button from "@/vue/common/Button";
import JobProfileHead from "./job-profile/Head"
import JobProfileFooter from "./job-profile/Footer"
import Onboarding from "./job-profile/Onboarding"
import {getQueryParams, removeQueryParamsFromUrl} from "@/helper/Url";
import {INITIATOR_CUSTOMER} from "@/store/communication/conversation/messages";
import {LIST_TYPE__CUSTOMER} from "@/store/communication/appointment";
import List from "@/vue/customer/job-profile/applicant/List.vue"
import SetJobProfileName from "@/vue/customer-area/SetJobProfileName.vue";
import JonInfo from "@/vue/common/JonInfo.vue";

export default {

	components: {
		JonInfo,
		SetJobProfileName,
		List,
		Navigation,
		Loading,
		Button,
		JobProfileHead,
		JobProfileFooter,
		Onboarding,
	},

	props: {
		jobProfileListItem64: {
			type: String,
			required: true,
		},
	},

	async mounted ()
	{
		const queryParams = getQueryParams();

		if (queryParams.showMessages || queryParams.showAppointments)
		{
			if (queryParams.showAppointments)
			{
				await this.openAppointmentFan({
					listType: LIST_TYPE__CUSTOMER,
					jobProfileId: this.jobProfileListItem.jobProfile.id
				});
			}

			if (queryParams.showMessages)
			{
				await this.openMessagesFan({
					customerData: {
						jobProfileListItem: this.jobProfileListItem,
					},
					initiator: INITIATOR_CUSTOMER
				});
			}

			removeQueryParamsFromUrl();
		}
	},

	data ()
	{
		const jobProfileListItem = JSON.parse(window.atob(this.jobProfileListItem64));

		return {
			loadingJobProfile: false,
			jobProfileListItem,
			initiallyLoaded: true,
			name: jobProfileListItem.jobProfile.name ?? '',
			saving: false,
			onlyFavorites: false,
			isPublished: jobProfileListItem.jobProfile.status.published,
		};
	},

	computed: {
		...mapState('meta', [
			'meta'
		]),
		...mapState('auth', [
			'customer',
			'internal'
		]),
		jobProfile ()
		{
			return this.jobProfileListItem.jobProfile;
		},
		hideNavigation ()
		{
			if (!this.customer || (this.loadingJobProfile && !this.initiallyLoaded))
			{
				return true;
			}

			return this.isOnboarding && this.hasOnlyOneJobProfile;
		},
		hasName ()
		{
			return !isEmptyOrNull(this.name);
		},
		hasOnlyOneJobProfile ()
		{
			return this.customer?.jobProfileCount === 1;
		},
		isOnboarding ()
		{
			if (this.internal)
			{
				return false;
			}

			return !this.jobProfileListItem.questionnaireFinished;
		}
	},

	methods: {
		...mapActions('communication/conversation/messages', {
			openMessagesFan: 'show',
		}),
		...mapActions('communication/appointment', {
			openAppointmentFan: 'show',
		}),
		...mapActions('auth', {
			loadAuth: 'load'
		}),
		onPublish ()
		{
			this.isPublished = true;
		},
		onUnpublish ()
		{
			this.isPublished = false;
		},
		hasMatches ()
		{
			return this.jobProfileListItem.matches > 0;
		},
		onCloseOnboarding ()
		{
			this.initiallyLoaded = false;

			this.loadJobProfile();

			// reload customer in auth, otherwise job profile name would not be filled in navigation
			this.loadAuth();
		},
		async onSaveName ()
		{
			await this.loadJobProfile();

			this.name = this.jobProfile.name;
		},
		resetOnlyFavoritesAndLoadJobProfile ()
		{
			this.onlyFavorites = false;
			this.loadJobProfile();
		},
		async loadJobProfile ()
		{
			const wasInitiallyLoaded = this.initiallyLoaded;

			this.loadingJobProfile = true;

			const response = await new GetList()
				.setId(this.jobProfile.id)
				.call();

			this.jobProfileListItem = response.data[0];

			this.isPublished = this.jobProfileListItem.jobProfile.status.published;

			if (wasInitiallyLoaded && this.$refs.list)
			{
				await this.$refs.list.loadApplicants();
			}

			this.loadingJobProfile = false;
			this.initiallyLoaded = true;
		},
	}
}
</script>
